import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "../components/ui/dropdown-menu";
import { Clock, Truck, Users, Activity, MoreHorizontal, Eye, RefreshCw, FileText, Scale } from "lucide-react";
import { apiRequest } from "../lib/queryClient";
import { getAuthHeaders } from "../lib/api";
import { getCurrentUser } from "../lib/auth";
import { ManualPlateModal } from "./manual-plate-modal";

interface TruckStatus {
  id: number;
  plateNumber: string;
  driverName: string;
  transporterName: string;
  customerName?: string;
  product?: string;
  expectedWeight?: string;
  orderExternalId?: string;
  status: 'pending_arrival' | 'checked_in' | 'staging' | 'departed';
  estimatedArrival?: string;
  actualArrival?: string;
  checkInTime?: string;
  departureTime?: string;
  timeInPark?: number; // minutes
  isNonMatched?: boolean;
  accessDecision?: string;
  startedAt?: string;
  endedAt?: string;

  // OCR Processing Fields
  ocrRequested?: boolean;
  ocrRequestedAt?: string;
  ocrProcessed?: boolean;
  ocrProcessedAt?: string;
  ocrConfidence?: number;

  // Driver Information from OCR
  driverPhone?: string;
  driverIdNumber?: string;
  driverPassportNumber?: string;
  driverDateOfBirth?: string;
  driverSex?: string;
  driverLicenseNumber?: string;
  driverLicenseValidTo?: string;

  // Order Information from OCR
  orderNumber?: string;
  clientName?: string;

  // Document URLs from OCR
  mineTicketUrl?: string;
  mineTicketNumber?: string;
  driverIdUrl?: string;
  driverPassportUrl?: string;
  driverLicenseUrl?: string;
  permit1Url?: string;
  permit1Number?: string;
  permit1ValidTo?: string;
  permit2Url?: string;
  permit2Number?: string;
  permit2ValidTo?: string;
}

interface Statistics {
  totalTrucksToday: number;
  totalTrucksHour: number;
  averageTimeInPark: number;
  activeStages: {
    pending_arrival: number;
    checked_in: number;
    staging: number;
    departed: number;
  };
}

const KanbanBoard = () => {
  const [, navigate] = useLocation();
  const [selectedTruck, setSelectedTruck] = useState<TruckStatus | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [expandedColumn, setExpandedColumn] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [boardPages, setBoardPages] = useState<{ [key: string]: number }>({}); // Track page per board
  const queryClient = useQueryClient();
  const currentUser = getCurrentUser();
  const [showManual, setShowManual] = useState(false);

  // const TRUCKS_PER_PAGE = 50;
  // const MAX_TRUCKS_PER_BOARD = 300;
  const TRUCKS_PER_PAGE = Number.MAX_SAFE_INTEGER;
  const MAX_TRUCKS_PER_BOARD = Number.MAX_SAFE_INTEGER;

  // Fetch truck data from existing visits and planned visits
  const { data: trucks = [], isLoading, dataUpdatedAt } = useQuery<TruckStatus[]>({
    queryKey: ["/api/kanban/trucks2"],
    queryFn: async () => {
      const response = await fetch("/api/kanban/trucks2", {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        console.error('Failed to fetch trucks:', response.statusText);
        return [];
      }
      const data = await response.json();

      return Array.isArray(data.trucks) ? data.trucks : [];
    },
    refetchInterval: 5000 // Refresh every 5 seconds for faster OCR updates
  });

  // Reset board pages when data refreshes
  React.useEffect(() => {
    if (dataUpdatedAt) {
      setBoardPages({});
    }
  }, [dataUpdatedAt]);

  // Fetch statistics
  const { data: stats } = useQuery<Statistics>({
    queryKey: ["/api/kanban/statistics"],
    queryFn: async () => {
      const response = await fetch("/api/kanban/statistics", {
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        console.error('Failed to fetch statistics:', response.statusText);
        return {
          totalTrucksToday: 0,
          totalTrucksHour: 0,
          averageTimeInPark: 0,
          activeStages: {
            pending_arrival: 0,
            checked_in: 0,
            staging: 0,
            departed: 0
          }
        };
      }
      return response.json();
    },
    refetchInterval: 30000 // Refresh every 30 seconds
  });


  // Update truck status mutation
  const updateStatusMutation = useMutation({
    mutationFn: async ({ truckId, newStatus }: { truckId: number; newStatus: string }) => {
      const response = await fetch(`/api/kanban/trucks/${truckId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status: newStatus }),
        headers: getAuthHeaders()
      });
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/kanban/trucks2"] });
      queryClient.invalidateQueries({ queryKey: ["/api/kanban/statistics"] });
    }
  });

  const manualOCRTriggerMutation = useMutation({
    mutationFn: async (truckId: number) => {
      const response = await fetch(`/api/kanban/trucks/${truckId}/trigger-ocr`, {
        method: "POST",
        headers: getAuthHeaders()
      });
      if (!response.ok) {
        throw new Error('Failed to trigger OCR');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/kanban/trucks2"] });
      queryClient.invalidateQueries({ queryKey: [`/api/kanban/trucks/${manualOCRTriggerMutation.variables}`] });
    },
    onError: (error) => {
      console.error('OCR trigger failed:', error);
    }
  });

  const handleStatusChange = (truckId: number, newStatus: string) => {
    updateStatusMutation.mutate({ truckId, newStatus });
  };

  const handleManualOCRTrigger = (truckId: number) => {
    manualOCRTriggerMutation.mutate(truckId);
  };

  const handleColumnClick = (columnId: string) => {
    setExpandedColumn(expandedColumn === columnId ? null : columnId);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["/api/kanban/trucks2"] }),
        queryClient.invalidateQueries({ queryKey: ["/api/kanban/statistics"] })
      ]);
    } catch (error) {
      console.error('Failed to refresh data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'staging': return 'bg-blue-100 border-blue-300 dark:bg-blue-900 dark:border-blue-600';
      case 'pending_arrival': return 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600';
      case 'checked_in': return 'bg-green-100 border-green-300 dark:bg-green-900 dark:border-green-600';
      case 'departed': return 'bg-purple-100 border-purple-300 dark:bg-purple-900 dark:border-purple-600';
      default: return 'bg-gray-100 border-gray-300 dark:bg-gray-800 dark:border-gray-600';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'staging': return '📦';
      case 'pending_arrival': return '🚛';
      case 'checked_in': return '✅';
      case 'departed': return '🎯';
      default: return '🚛';
    }
  };

  const getTripDurationMinutes = (truck: TruckStatus) => {
    if (!truck.startedAt) return null;

    const start = new Date(truck.startedAt).getTime();
    const end = truck.endedAt
      ? new Date(truck.endedAt).getTime()
      : Date.now(); // still ongoing

    const diffMs = end - start;
    return Math.floor(diffMs / (1000 * 60)); // minutes
  };

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  //const isInvalidPlate = (plate?: string) => !!plate && plate.toUpperCase().startsWith("INVALID");
  const isInvalidPlate = (plate?: string) => {
    if (!plate) return false;

    const upper = plate.toUpperCase();

    // Case 1: our system-generated invalid plates
    if (upper.startsWith("INVALID")) return true;

    // Case 2: OCR / ANPR plates containing # placeholders
    if (upper.includes("#")) return true;

    return false;
  };


  // Ensure trucks is always an array for safety
  const safeTrucks = Array.isArray(trucks) ? trucks : [];

  const getVisibleTrucksForBoard = (status: string) => {
    let boardTrucks = safeTrucks.filter(t => t.status === status);
    if (status === "departed") {
      boardTrucks = boardTrucks.filter(t => !isInvalidPlate(t.plateNumber));
      boardTrucks = boardTrucks.sort((a, b) => {

        const aTime = a.endedAt ? new Date(a.endedAt).getTime() : 0;
        const bTime = b.endedAt ? new Date(b.endedAt).getTime() : 0;
        return bTime - aTime; // newest at the top
      });
    }
    const page = boardPages[status] || 1;
    const limit = page * TRUCKS_PER_PAGE;
    const hasMore = boardTrucks.length > limit && limit < MAX_TRUCKS_PER_BOARD;

    return {
      trucks: boardTrucks.slice(0, limit),
      total: boardTrucks.length,
      hasMore,
      visibleCount: Math.min(limit, boardTrucks.length)
    };
  };

  const loadMoreForBoard = (status: string) => {
    setBoardPages(prev => ({
      ...prev,
      [status]: (prev[status] || 1) + 1
    }));
  };

  React.useEffect(() => {
    if (!Array.isArray(trucks)) return;

    const ids = trucks.map(t => t.id);
    const seen = new Set<number>();
    const dups = new Set<number>();

    for (const id of ids) {
      if (seen.has(id)) dups.add(id);
      else seen.add(id);
    }

    if (dups.size > 0) {
      console.warn('🚨 Duplicate truck IDs from API:', Array.from(dups));
    }
  }, [trucks]);

  const allStages = [
    {
      id: 'staging',
      title: 'Staging',
      count: stats?.activeStages?.staging || 0,
      color: 'blue'
    },
    {
      id: 'pending_arrival',
      title: 'Pending Arrival',
      count: stats?.activeStages?.pending_arrival || 0,
      color: 'gray'
    },
    {
      id: 'checked_in',
      title: 'Checked In',
      count: stats?.activeStages?.checked_in || 0,
      color: 'green'
    },
    {
      id: 'departed',
      title: 'Departed',
      count: stats?.activeStages?.departed || 0,
      color: 'purple'
    }
  ];

  // filter stages based on user role
  let stages;
  if (currentUser?.role === 'mine') {
    stages = allStages.filter(stage => stage.id === 'pending_arrival');
  } else if (currentUser?.role === 'staging') {
    stages = allStages.filter(stage =>
      stage.id === 'pending_arrival' || stage.id === 'checked_in' || stage.id === 'departed'
    );
  } else if (currentUser?.role === 'port') {
    stages = allStages;
  } else {
    stages = allStages;
  }

  return (
    <div>
      <div className="container mx-auto p-4 space-y-6" data-testid="kanban-board">
        {/* Header with Board Selection and Statistics */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              {currentUser?.role === 'mine' ? 'Mine Truck Arrivals' :
                currentUser?.role === 'staging' ? 'Staging Operations Board' :
                  currentUser?.role === 'port' ? 'Port Operations Board' :
                    'Truck Operations Board'}
            </h1>
            {currentUser?.role === 'mine' && (
              <Badge variant="outline" className="text-sm">
                Mine View - Pending Arrivals Only
              </Badge>
            )}
            {currentUser?.role === 'staging' && (
              <Badge variant="outline" className="text-sm">
                Staging View - All Mines
              </Badge>
            )}
            {currentUser?.role === 'port' && (
              <Badge variant="outline" className="text-sm">
                Port View - Full Operations
              </Badge>
            )}
          </div>
          <div className="flex gap-4">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-2"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh'}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowManual(true)}
              className="bg-blue-600 text-white px-4 py-2 rounded"
            >
              Manual Entry
            </Button>
            <ManualPlateModal open={showManual} onClose={() => setShowManual(false)} />
            <Card className="px-4 py-2">
              <div className="flex items-center gap-2">
                <Truck className="h-4 w-4 text-blue-600" />
                <div>
                  <p className="text-sm font-medium" data-testid="text-trucks-today">
                    {stats?.totalTrucksToday || 0} Today
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Trucks</p>
                </div>
              </div>
            </Card>
            <Card className="px-4 py-2">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-green-600" />
                <div>
                  <p className="text-sm font-medium" data-testid="text-trucks-hour">
                    {stats?.totalTrucksHour || 0} This Hour
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Trucks/Hour</p>
                </div>
              </div>
            </Card>
            <Card className="px-4 py-2">
              <div className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-orange-600" />
                <div>
                  <p className="text-sm font-medium" data-testid="text-avg-time">
                    {stats?.averageTimeInPark ? formatTime(stats.averageTimeInPark) : '0m'}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Avg. Time in Park</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Kanban Board */}
        {expandedColumn ? (
          // Expanded Column View
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setExpandedColumn(null)}
                  data-testid="button-close-expanded"
                >
                  ← Back to Board
                </Button>
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{getStatusIcon(expandedColumn)}</span>
                  <h2 className="text-2xl font-bold">
                    {stages.find(s => s.id === expandedColumn)?.title}
                  </h2>
                  <Badge variant="secondary" className="text-lg px-3 py-1">
                    {safeTrucks.filter(truck => truck.status === expandedColumn).length}
                  </Badge>
                </div>
              </div>
            </div>

            <Card className="min-h-[400px] max-h-[calc(100vh-200px)]">
              <CardContent className="p-0">
                <div className="overflow-auto max-h-[calc(100vh-250px)]">
                  {(() => {
                    const { trucks: boardTrucks, hasMore, visibleCount, total } = getVisibleTrucksForBoard(expandedColumn!);
                    return boardTrucks.length > 0 ? (
                      <div className="relative">
                        <table className="w-full text-sm">
                          <thead className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 z-10">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Truck & Driver</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Product & Order</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Timing</th>
                              <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 dark:divide-gray-700 bg-white dark:bg-gray-900">
                            {boardTrucks
                              .map((truck, index, array) => (
                                <tr
                                  key={`${truck.id}-${expandedColumn}-${index}`}
                                  className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${index === array.length - 1 ? 'pb-4' : ''
                                    }`}
                                  data-testid={`expanded-truck-${truck.id}`}
                                >
                                  {/* Truck & Driver Column */}
                                  <td className="px-3 py-2">
                                    <div className="flex items-center space-x-2">
                                      <div className="flex-shrink-0">
                                        <div className="w-6 h-6 bg-blue-100 dark:bg-blue-900 rounded flex items-center justify-center">
                                          <Truck className="w-3 h-3 text-blue-600 dark:text-blue-400" />
                                        </div>
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate" data-testid={`expanded-plate-${truck.id}`}>
                                            {truck.plateNumber}
                                          </p>
                                          {truck.ocrRequested && (
                                            <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 border-blue-400">
                                              📄 OCR
                                            </Badge>
                                          )}
                                          {currentUser?.role === 'staging' && truck.customerName?.includes('-') && (
                                            <Badge variant="default" className="text-xs bg-blue-600 text-white font-mono">
                                              {truck.customerName.split('-')[0].trim()}
                                            </Badge>
                                          )}
                                        </div>
                                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate" data-testid={`expanded-driver-${truck.id}`}>
                                          {truck.driverName}
                                        </p>
                                      </div>
                                    </div>
                                  </td>

                                  {/* Product & Order Column */}
                                  <td className="px-3 py-2">
                                    <div className="space-y-0.5">
                                      {truck.product ? (
                                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400 truncate" data-testid={`expanded-product-${truck.id}`}>
                                          {truck.product}
                                        </p>
                                      ) : (
                                        <p className="text-xs text-gray-400 dark:text-gray-500">No product</p>
                                      )}
                                      {truck.orderExternalId && (
                                        <p className="text-xs text-gray-600 dark:text-gray-400 truncate" data-testid={`expanded-order-${truck.id}`}>
                                          #{truck.orderExternalId}
                                        </p>
                                      )}
                                    </div>
                                  </td>

                                  {/* Timing Column */}
                                  <td className="px-3 py-2">
                                    <div className="space-y-0.5">
                                      {truck.actualArrival && (
                                        <div className="flex items-center space-x-1">
                                          <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                                          <p className="text-xs text-gray-600 dark:text-gray-400" data-testid={`expanded-arrival-${truck.id}`}>
                                            {new Date(truck.actualArrival).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                          </p>
                                        </div>
                                      )}
                                      
                                      {(truck.status === 'departed' ? truck.endedAt : truck.actualArrival) && (
                                        <Badge variant="outline" className="text-xs">
                                          {new Date(truck.status === 'departed' ? truck.endedAt! : truck.actualArrival!).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '')}
                                        </Badge>
                                      )}
                                    </div>
                                  </td>

                                  {/* Actions Column */}
                                  <td className="px-3 py-2">
                                    <div className="flex items-center justify-center space-x-1">
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => {
                                          setSelectedTruck(truck);
                                          setIsDetailOpen(true);
                                        }}
                                        data-testid={`button-view-details-${truck.id}`}
                                        className="h-6 w-6 p-0"
                                      >
                                        <Eye className="h-3 w-3" />
                                      </Button>

                                      <DropdownMenu>
                                        <DropdownMenuTrigger asChild>
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                          >
                                            <MoreHorizontal className="h-3 w-3" />
                                          </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end" className="w-48">
                                          <DropdownMenuItem
                                            onClick={() => navigate(`/control/${truck.id}`)}
                                            data-testid={`button-control-${truck.id}`}
                                            className="text-[hsl(187,100%,42%)]"
                                          >
                                            <Scale className="w-4 h-4 mr-2" />
                                            Weighbridge Control
                                          </DropdownMenuItem>
                                          <DropdownMenuSeparator />
                                          {stages.map((nextStage) => (
                                            <DropdownMenuItem
                                              key={nextStage.id}
                                              onClick={() => handleStatusChange(truck.id, nextStage.id)}
                                              disabled={updateStatusMutation.isPending}
                                              data-testid={`button-move-${nextStage.id}-${truck.id}`}
                                              className={truck.status === nextStage.id ? "bg-blue-50 text-blue-700" : ""}
                                            >
                                              <span className="mr-2">{getStatusIcon(nextStage.id)}</span>
                                              {nextStage.title}
                                            </DropdownMenuItem>
                                          ))}
                                        </DropdownMenuContent>
                                      </DropdownMenu>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>

                        {/* Load More button for expanded view */}
                        {hasMore && (
                          <div className="pt-4 pb-4 flex justify-center">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMoreForBoard(expandedColumn!)}
                              className="text-xs"
                            >
                              Load More ({visibleCount}/{total})
                            </Button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                          <Truck className="h-12 w-12 mx-auto mb-4 opacity-50" />
                          <h3 className="text-lg font-medium mb-2">No trucks in this stage</h3>
                          <p className="text-sm">Trucks will appear here when they reach this stage</p>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </CardContent>
            </Card>
          </div>
        ) : (
          // Normal Kanban View
          <div className={`grid gap-4 h-[calc(100vh-250px)] ${stages.length === 1 ? 'grid-cols-1' :
            stages.length === 3 ? 'grid-cols-3' :
              'grid-cols-4'
            }`}>
            {stages.map((stage) => (
              <Card key={stage.id} className={`flex flex-col ${getStatusColor(stage.id)}`}>
                <CardHeader className="pb-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors" onClick={() => handleColumnClick(stage.id)}>
                  <CardTitle className="flex items-center justify-between text-sm font-medium">
                    <div className="flex items-center gap-2">
                      <span>{getStatusIcon(stage.id)}</span>
                      <span>{stage.title}</span>
                    </div>
                    <Badge variant="secondary" data-testid={`badge-count-${stage.id}`}>
                      {stage.count}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex-1 overflow-y-auto space-y-2">
                  {(() => {
                    const { trucks: boardTrucks, hasMore, visibleCount, total } = getVisibleTrucksForBoard(stage.id);
                    return (
                      <>
                        {boardTrucks.map((truck, index) => {
                          const invalid = isInvalidPlate(truck.plateNumber);
                          return (

                            <Dialog
                              key={`${truck.id}-${stage.id}-${index}`}
                              open={isDetailOpen && selectedTruck?.id === truck.id}
                              onOpenChange={setIsDetailOpen}
                            >
                              <DialogTrigger asChild>
                                <Card
                                  className={`p-3 cursor-pointer hover:shadow-md transition-shadow border ${invalid
                                    ? 'bg-red-100 dark:bg-red-900 border-red-400 dark:border-red-600'
                                    : truck.isNonMatched
                                      ? 'bg-orange-100 dark:bg-orange-900 border-orange-300 dark:border-orange-600'
                                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                                    }`}
                                  onClick={() => setSelectedTruck(truck)}
                                  data-testid={`truck-card-${truck.id}`}
                                >
                                  <div className="space-y-2">
                                    {/* Invalid Plate Badge */}
                                    {invalid && (
                                      <div className="flex">
                                        <Badge
                                          variant="outline"
                                          className="text-xs bg-red-200 text-red-900 border-red-500 font-semibold"
                                        >
                                          ❗ Invalid Plate
                                        </Badge>
                                      </div>
                                    )}
                                    {/* Non-matched ANPR Badge */}
                                    {truck.isNonMatched && (
                                      <div className="flex">
                                        <Badge variant="outline" className="text-xs bg-orange-200 text-orange-800 border-orange-400 font-semibold">
                                          🚨 Non-Matched ANPR
                                        </Badge>
                                      </div>
                                    )}

                                    {/* Mine Reference Badge for Staging Users */}
                                    {currentUser?.role === 'staging' && truck.customerName?.includes('-') && (
                                      <div className="flex">
                                        <Badge variant="default" className="text-xs bg-blue-600 text-white font-mono">
                                          {truck.customerName.split('-')[0].trim()}
                                        </Badge>
                                      </div>
                                    )}

                                    {/* OCR Requested Badge */}
                                    {truck.ocrRequested && (
                                      <div className="flex">
                                        <Badge variant="outline" className="text-xs bg-blue-100 text-blue-800 border-blue-400">
                                          📄 OCR Requested
                                        </Badge>
                                      </div>
                                    )}

                                    <div className="flex justify-between items-start">
                                      <div>
                                        <p className="font-semibold text-sm" data-testid={`text-plate-${truck.id}`}>
                                          {truck.plateNumber}
                                        </p>
                                        <p className="text-xs text-gray-600 dark:text-gray-400" data-testid={`text-driver-${truck.id}`}>
                                          {truck.driverName}
                                        </p>
                                      </div>
                                      {/* {truck.timeInPark ? (
                                        <Badge variant="outline" className="text-xs">
                                          {formatTime(truck.timeInPark)} 
                                        </Badge>
                                      ) : null} */}
                                      {(truck.status === 'departed' ? truck.endedAt : truck.actualArrival) && (
                                        <Badge variant="outline" className="text-xs">
                                          {new Date(truck.status === 'departed' ? truck.endedAt! : truck.actualArrival!).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '')}
                                        </Badge>
                                      )}
                                    </div>
                                    <div>
                                      <p className="text-xs text-gray-600 dark:text-gray-400" data-testid={`text-transporter-${truck.id}`}>
                                        {truck.transporterName}
                                      </p>
                                      {truck.product && (
                                        <p className="text-xs font-medium text-blue-600 dark:text-blue-400">
                                          {truck.product}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                </Card>
                              </DialogTrigger>

                              {/* Truck Detail Popup */}
                              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid={`dialog-truck-details-${truck.id}`}>
                                <DialogHeader>
                                  <DialogTitle className="flex items-center gap-2">
                                    <Truck className="h-5 w-5" />
                                    Truck Details - {truck.plateNumber}
                                  </DialogTitle>
                                </DialogHeader>

                                <div className="space-y-6">
                                  {/* Vehicle Information Table */}
                                  <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                      <Truck className="h-5 w-5 text-blue-600" />
                                      Vehicle Information
                                    </h4>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      <div className="space-y-3">
                                        <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Plate Number</span>
                                          <span className="font-semibold text-lg" data-testid="dialog-plate-number">{truck.plateNumber}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Driver Name</span>
                                          <span className="font-medium" data-testid="dialog-driver-name">{truck.driverName}</span>
                                        </div>
                                        <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Transporter</span>
                                          <span className="font-medium" data-testid="dialog-transporter-name">{truck.transporterName}</span>
                                        </div>
                                      </div>
                                      <div className="space-y-3">
                                        <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Current Status</span>
                                          <Badge variant="secondary" className="text-sm">
                                            {getStatusIcon(truck.status)} {truck.status.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                          </Badge>
                                        </div>
                                        {/* {truck.timeInPark && (
                                          <div className="flex justify-between items-center py-2 border-b border-gray-200 dark:border-gray-700">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Time in Park</span>
                                            <span className="font-medium text-orange-600" data-testid="dialog-time-in-park">
                                              {formatTime(truck.timeInPark) }
                                            </span>
                                          </div>
                                        )} */}
                                        {(truck.status === 'departed' ? truck.endedAt : truck.actualArrival) && (
                                          <Badge variant="outline" className="text-xs">
                                          {new Date(truck.status === 'departed' ? truck.endedAt! : truck.actualArrival!).toLocaleString('sv-SE', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).replace(',', '')}
                                        </Badge>
                                        )}
                                      </div>
                                    </div>
                                  </div>

                                  {/* Order Information Table */}
                                  {(truck.orderExternalId || truck.product || truck.expectedWeight) && (
                                    <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                      <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                        <Activity className="h-5 w-5 text-blue-600" />
                                        Order Information
                                      </h4>
                                      <div className="space-y-3">
                                        {truck.orderExternalId && (
                                          <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Order ID</span>
                                            <span className="font-medium text-blue-600" data-testid="dialog-order-id">#{truck.orderExternalId}</span>
                                          </div>
                                        )}
                                        {truck.product && (
                                          <div className="flex justify-between items-center py-2 border-b border-blue-200 dark:border-blue-700">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Product</span>
                                            <span className="font-medium" data-testid="dialog-product">{truck.product}</span>
                                          </div>
                                        )}
                                        {truck.expectedWeight && (
                                          <div className="flex justify-between items-center py-2">
                                            <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Expected Weight</span>
                                            <span className="font-medium text-green-600" data-testid="dialog-expected-weight">{truck.expectedWeight}</span>
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* OCR Processing Information */}
                                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                      <FileText className="h-5 w-5 text-purple-600" />
                                      OCR Processing Information
                                    </h4>
                                    <div className="space-y-3">
                                      <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-700">
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">OCR Requested</span>
                                        <div className="flex items-center gap-2">
                                          <Badge variant={truck.ocrRequested ? "default" : "secondary"} className="text-sm">
                                            {truck.ocrRequested ? "Yes" : "No"}
                                          </Badge>
                                          {truck.ocrRequestedAt && (
                                            <span className="text-xs text-gray-500">
                                              {new Date(truck.ocrRequestedAt).toLocaleString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                      <div className="flex justify-between items-center py-2 border-b border-purple-200 dark:border-purple-700">
                                        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">OCR Status</span>
                                        <div className="flex items-center gap-2">
                                          <Badge variant={truck.ocrProcessed ? "default" : "secondary"} className="text-sm">
                                            {truck.ocrProcessed ? "Processed" : "Pending"}
                                          </Badge>
                                          {truck.ocrProcessedAt && (
                                            <span className="text-xs text-gray-500">
                                              {new Date(truck.ocrProcessedAt).toLocaleString()}
                                            </span>
                                          )}
                                        </div>
                                      </div>

                                      {/* Driver Information from OCR */}
                                      <div className="space-y-2">
                                        <h5 className="text-sm font-semibold text-purple-700 dark:text-purple-300">Driver Information</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Name</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverName || <span className="text-gray-400 italic">Not available</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Phone</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverPhone || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">ID Number</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverIdNumber || truck.driverId || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Date of Birth</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverDateOfBirth || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Sex</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverSex || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Passport</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverPassportNumber || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">License Number</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverLicenseNumber || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">License Valid To</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverLicenseValidTo || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Order Information from OCR */}
                                      <div className="space-y-2">
                                        <h5 className="text-sm font-semibold text-purple-700 dark:text-purple-300">Order Information</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Order Number</span>
                                            <span className="text-xs font-medium">
                                              {truck.orderNumber || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Client Name</span>
                                            <span className="text-xs font-medium">
                                              {truck.clientName || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {/* Document URLs from OCR */}
                                      <div className="space-y-2">
                                        <h5 className="text-sm font-semibold text-purple-700 dark:text-purple-300">Document Uploads</h5>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Mine Ticket</span>
                                            <span className="text-xs font-medium">
                                              {truck.mineTicketNumber || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Driver ID</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverIdUrl ? (
                                                <a href={truck.driverIdUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                  View Document
                                                </a>
                                              ) : (
                                                <span className="text-gray-400 italic">Pending OCR</span>
                                              )}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Driver License</span>
                                            <span className="text-xs font-medium">
                                              {truck.driverLicenseUrl ? (
                                                <a href={truck.driverLicenseUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                                                  View Document
                                                </a>
                                              ) : (
                                                <span className="text-gray-400 italic">Pending OCR</span>
                                              )}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Permit 1</span>
                                            <span className="text-xs font-medium">
                                              {truck.permit1Number || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Permit 1 Valid To</span>
                                            <span className="text-xs font-medium">
                                              {truck.permit1ValidTo || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Permit 2</span>
                                            <span className="text-xs font-medium">
                                              {truck.permit2Number || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                          <div className="flex justify-between items-center py-1">
                                            <span className="text-xs text-gray-600 dark:text-gray-400">Permit 2 Valid To</span>
                                            <span className="text-xs font-medium">
                                              {truck.permit2ValidTo || <span className="text-gray-400 italic">Pending OCR</span>}
                                            </span>
                                          </div>
                                        </div>
                                      </div>

                                      {truck.ocrProcessedAt && (
                                        <div className="flex justify-between items-center py-2 border-t border-purple-200 dark:border-purple-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Processed At</span>
                                          <span className="text-sm font-medium text-purple-600">
                                            {new Date(truck.ocrProcessedAt).toLocaleString()}
                                          </span>
                                        </div>
                                      )}

                                      {truck.ocrConfidence && (
                                        <div className="flex justify-between items-center py-2">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Confidence Score</span>
                                          <span className="text-sm font-medium text-purple-600">
                                            {truck.ocrConfidence}%
                                          </span>
                                        </div>
                                      )}

                                      {/* Manual OCR Trigger Button - Only for staging users in checked_in board */}
                                      {currentUser?.role === 'staging' && truck.status === 'checked_in' && (
                                        <div className="flex justify-center pt-4 border-t border-purple-200 dark:border-purple-700">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => handleManualOCRTrigger(truck.id)}
                                            disabled={manualOCRTriggerMutation.isPending || truck.ocrProcessed}
                                            className={`flex items-center gap-2 border-purple-300 ${truck.ocrProcessed
                                              ? 'text-gray-400 border-gray-300 cursor-not-allowed'
                                              : 'text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/30'
                                              }`}
                                          >
                                            <RefreshCw className={`h-4 w-4 ${manualOCRTriggerMutation.isPending ? 'animate-spin' : ''}`} />
                                            {manualOCRTriggerMutation.isPending
                                              ? 'Triggering OCR...'
                                              : truck.ocrProcessed
                                                ? 'OCR Already Processed'
                                                : 'Manual Trigger OCR'
                                            }
                                          </Button>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Timing Information Table */}
                                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                      <Clock className="h-5 w-5 text-green-600" />
                                      Timing Information
                                    </h4>
                                    <div className="space-y-3">
                                      {truck.estimatedArrival && (
                                        <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Estimated Arrival</span>
                                          <span className="font-medium" data-testid="dialog-eta">
                                            {new Date(truck.estimatedArrival).toLocaleTimeString()}
                                          </span>
                                        </div>
                                      )}
                                      {truck.actualArrival && (
                                        <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Actual Arrival</span>
                                          <span className="font-medium text-green-600" data-testid="dialog-arrival">
                                            {new Date(truck.actualArrival).toLocaleTimeString()}
                                          </span>
                                        </div>
                                      )}
                                      {truck.checkInTime && (
                                        <div className="flex justify-between items-center py-2 border-b border-green-200 dark:border-green-700">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Check-in Time</span>
                                          <span className="font-medium" data-testid="dialog-checkin">
                                            {new Date(truck.checkInTime).toLocaleTimeString()}
                                          </span>
                                        </div>
                                      )}
                                      {truck.departureTime && (
                                        <div className="flex justify-between items-center py-2">
                                          <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Departure Time</span>
                                          <span className="font-medium text-purple-600" data-testid="dialog-departure">
                                            {new Date(truck.departureTime).toLocaleTimeString()}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Weighbridge Control Action */}
                                  <div className="bg-[hsl(187,100%,42%)]/10 dark:bg-[hsl(187,100%,42%)]/10 rounded-lg p-4">
                                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2 text-[hsl(187,100%,42%)]">
                                      <Scale className="h-5 w-5" />
                                      Weighbridge Operations
                                    </h4>
                                    <Button
                                      className="w-full bg-[hsl(187,100%,42%)] hover:bg-[hsl(187,100%,35%)] text-white"
                                      onClick={() => {
                                        setIsDetailOpen(false);
                                        navigate(`/control/${truck.id}`);
                                      }}
                                      data-testid={`button-weighbridge-control-${truck.id}`}
                                    >
                                      <Scale className="h-4 w-4 mr-2" />
                                      Open Weighbridge Control Panel
                                    </Button>
                                  </div>

                                  {/* Status Actions */}
                                  <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                                    <h4 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                      <Users className="h-5 w-5 text-orange-600" />
                                      Status Actions
                                    </h4>
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                      {stages.map((nextStage) => (
                                        <Button
                                          key={nextStage.id}
                                          variant={truck.status === nextStage.id ? "default" : "outline"}
                                          size="sm"
                                          onClick={() => handleStatusChange(truck.id, nextStage.id)}
                                          disabled={updateStatusMutation.isPending}
                                          data-testid={`button-status-${nextStage.id}`}
                                          className={`flex flex-col items-center gap-1 h-auto py-3 ${truck.status === nextStage.id
                                            ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                            : 'hover:bg-orange-100 dark:hover:bg-orange-900/30'
                                            }`}
                                        >
                                          <span className="text-lg">{getStatusIcon(nextStage.id)}</span>
                                          <span className="text-xs font-medium">{nextStage.title}</span>
                                        </Button>
                                      ))}
                                    </div>
                                  </div>
                                </div>
                              </DialogContent>
                            </Dialog>
                          )
                        })}

                        {/* Show Load More button if there are more trucks */}
                        {hasMore && (
                          <div className="pt-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => loadMoreForBoard(stage.id)}
                              className="w-full text-xs"
                              data-testid={`button-load-more-${stage.id}`}
                            >
                              Load More ({visibleCount}/{total})
                            </Button>
                          </div>
                        )}
                      </>
                    );
                  })()}

                  {getVisibleTrucksForBoard(stage.id).trucks.length === 0 && (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Truck className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">No trucks in this stage</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

      </div>
    </div>
  );
};

export default KanbanBoard;